import './RootLayout.css';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import ButtonAppBar from './Header';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { useEffect, useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { HeaderProvider } from '../../context/headerContext';
import { AttentionProvider } from '../../context/attentionContext';
import { PizzaBranchProvider } from '../../context/pizzaBranchContext';
import PizzaAttentionWatcher from '../Attention/PizzaAttentionWatcher';
import FeedbackOverdueWatcher from '../Attention/FeedbackOverdueWatcher';
import TicketScanWedgeListener from '../components/TicketScanWedgeListener';
import CallProtocolListener from '../CallProtocol/CallProtocolListener';
import { useAuth } from '../../context/authContext';
import { useColorMode } from '../../context/colorModeContext';
import { createManagementTheme } from '../../theme/managementTheme';
import ThemeIntroductionModal from './ThemeIntroductionModal';
import NewFrontendIntroductionModal from './NewFrontendIntroductionModal';
import LegacyFrontendSunsetModal from './LegacyFrontendSunsetModal';
import LegacyFeedbackPrompt from './LegacyFeedbackPrompt';
import FrontendUsageTracker from './FrontendUsageTracker';
import AuthenticationModal from '../views/AuthenticationModal';
import { resolveViewTitle } from '../../util/viewTitle';

const MANAGEMENT_SUFFIX = 'Management';
const DEFAULT_DOCUMENT_TITLE = `FunZone - ${MANAGEMENT_SUFFIX}`;

function TitleManager() {
  const location = useLocation();
  const auth = useAuth();

  const titlePrefix = useMemo(() => {
    const roles = auth?.currentUser?.roles;
    const isSuperadmin =
      Array.isArray(roles) && roles.some(r => String(r || '').trim().toLowerCase() === 'superadmin');
    if (!isSuperadmin) return '';

    const isStaging = import.meta.env.VITE_IS_STAGING === 'true';
    const isDev = import.meta.env.VITE_IS_DEV === 'true' || Boolean(import.meta.env.DEV);
    const isProdBuild = Boolean(import.meta.env.PROD);
    const isLive = isProdBuild && !isStaging && !isDev;

    const envLetter = isLive ? 'L' : isStaging ? 'S' : 'D';
    return `(${envLetter}) `;
  }, [auth?.currentUser?.roles]);

  const viewTitle = useMemo(() => {
    return resolveViewTitle({ pathname: location?.pathname, menuItems: auth?.menuItems });
  }, [location?.pathname, auth?.menuItems]);

  useEffect(() => {
    if (viewTitle) {
      document.title = `${titlePrefix}${viewTitle} - ${MANAGEMENT_SUFFIX}`;
      return;
    }
    document.title = `${titlePrefix}${DEFAULT_DOCUMENT_TITLE}`;
  }, [viewTitle, titlePrefix]);

  return null;
}

function AuthenticationModalHost() {
  const auth = useAuth();
  const config = auth?.authModalConfig;

  if (!auth?.showAuthModal || !config) return null;

  return (
    <AuthenticationModal
      isOpen={auth.showAuthModal}
      onClose={() => auth.setAuthModal?.(false)}
      mode={config.mode}
      eligibleUsers={config.eligibleUsers}
      requiredRights={config.requiredRights || []}
      onAuthenticationSuccess={config.onAuthenticationSuccess}
      onCancel={config.onCancel}
      isCounterDevice={auth.isCounterDevice}
      displayInfo={config.displayInfo || null}
    />
  );
}

function App() {
  const { resolvedPaletteMode } = useColorMode();
  const theme = useMemo(() => createManagementTheme(resolvedPaletteMode), [resolvedPaletteMode]);

  return (
    <>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <HeaderProvider>
          <AttentionProvider>
            <PizzaBranchProvider>
              <TitleManager />
              <FrontendUsageTracker />
              <ThemeIntroductionModal />
              <NewFrontendIntroductionModal />
              <LegacyFrontendSunsetModal />
              <LegacyFeedbackPrompt />
              <ButtonAppBar>
                <Outlet />
              </ButtonAppBar>
              <AuthenticationModalHost />
              <PizzaAttentionWatcher />
              <FeedbackOverdueWatcher />
              <TicketScanWedgeListener />
              <CallProtocolListener />
            </PizzaBranchProvider>
          </AttentionProvider>
        </HeaderProvider>
      </ThemeProvider>
    </>
  );
}

export default App;
